import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { 
  Video, 
  MessageSquare, 
  Clock, 
  Calendar,
  User,
  Star,
  CheckCircle,
  ChevronRight,
  Info,
  Stethoscope
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format } from 'date-fns';

export interface TeleTriageConnectProps {
  symptoms?: string[];
  urgencyLevel: 'urgent' | 'standard';
  className?: string;
}

interface Provider {
  id: string;
  name: string;
  specialty: string;
  avatar?: string;
  rating: number;
  availableIn: string;
  badges?: string[];
}

export function TeleTriageConnect({
  symptoms = [],
  urgencyLevel,
  className
}: TeleTriageConnectProps) {
  const [activeTab, setActiveTab] = useState<string>('now');
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [connectionInitiated, setConnectionInitiated] = useState<boolean>(false);

  const availableProviders: Provider[] = [
    {
      id: 'p1',
      name: 'Dr. Sarah Johnson',
      specialty: 'Emergency Medicine',
      avatar: '/avatars/doctor-1.jpg',
      rating: 4.9,
      availableIn: 'Available now',
      badges: ['Urgent Care Specialist']
    },
    {
      id: 'p2',
      name: 'Dr. Michael Chen',
      specialty: 'Family Medicine',
      avatar: '/avatars/doctor-2.jpg',
      rating: 4.8,
      availableIn: '5 minutes',
      badges: ['Multilingual']
    },
    {
      id: 'p3',
      name: 'Dr. Amara Patel',
      specialty: 'Internal Medicine',
      avatar: '/avatars/doctor-3.jpg',
      rating: 4.7,
      availableIn: '10 minutes'
    }
  ];

  const scheduledProviders: Provider[] = [
    {
      id: 'p4',
      name: 'Dr. Robert Williams',
      specialty: 'Emergency Medicine',
      avatar: '/avatars/doctor-4.jpg',
      rating: 4.9,
      availableIn: 'Today, 2:00 PM'
    },
    {
      id: 'p5',
      name: 'Dr. Lisa Martinez',
      specialty: 'Family Medicine',
      avatar: '/avatars/doctor-5.jpg',
      rating: 4.8,
      availableIn: 'Today, 3:30 PM'
    },
    {
      id: 'p6',
      name: 'Dr. James Thompson',
      specialty: 'Urgent Care',
      avatar: '/avatars/doctor-6.jpg',
      rating: 4.7,
      availableIn: 'Today, 5:00 PM'
    }
  ];

  const handleProviderSelect = (providerId: string) => {
    setSelectedProvider(providerId);
  };

  const handleConnect = () => {
    if (selectedProvider) {
      // In a real implementation, this would initiate a telehealth connection
      setConnectionInitiated(true);
      // Simulate redirect to video call after a short delay
      setTimeout(() => {
        // This would navigate to the video call interface
        console.log(`Connecting to provider ${selectedProvider}`);
      }, 1500);
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={cn(
              "w-3 h-3",
              i < Math.floor(rating) ? "text-yellow-400 fill-yellow-400" : "text-gray-300"
            )}
          />
        ))}
        <span className="ml-1 text-xs text-gray-600">{rating}</span>
      </div>
    );
  };

  return (
    <Card className={cn('border-2', urgencyLevel === 'urgent' ? 'border-orange-500' : 'border-yellow-500', className)}>
      <CardHeader className={urgencyLevel === 'urgent' ? 'bg-orange-50' : 'bg-yellow-50'}>
        <div className="flex items-center gap-2">
          <Stethoscope className={cn('w-6 h-6', urgencyLevel === 'urgent' ? 'text-orange-600' : 'text-yellow-600')} />
          <CardTitle className={cn(urgencyLevel === 'urgent' ? 'text-orange-800' : 'text-yellow-800')}>
            {urgencyLevel === 'urgent' ? 'Urgent Tele-Triage' : 'Medical Tele-Consultation'}
          </CardTitle>
        </div>
        <CardDescription className="text-base">
          {urgencyLevel === 'urgent' 
            ? 'Connect with a healthcare provider immediately for urgent medical guidance.' 
            : 'Speak with a healthcare provider about your symptoms and get professional advice.'}
        </CardDescription>
      </CardHeader>
      
      <Tabs defaultValue="now" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-2 mx-4 mt-4">
          <TabsTrigger value="now" className="flex items-center gap-2">
            <Video className="w-4 h-4" />
            <span>Available Now</span>
          </TabsTrigger>
          <TabsTrigger value="schedule" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span>Schedule</span>
          </TabsTrigger>
        </TabsList>
        
        <CardContent className="pt-6">
          {connectionInitiated && (
            <div className="mb-4 flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-md">
              <CheckCircle className="w-5 h-5" />
              <span>Connecting to your provider... Please wait.</span>
            </div>
          )}
          
          <TabsContent value="now" className="space-y-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <h3 className="font-medium text-lg mb-3">Available Healthcare Providers</h3>
              
              <div className="space-y-4">
                {availableProviders.map((provider) => (
                  <div 
                    key={provider.id} 
                    className={cn(
                      "border rounded-md p-3 space-y-2 cursor-pointer transition-colors",
                      selectedProvider === provider.id 
                        ? "border-blue-500 bg-blue-50" 
                        : "border-gray-200 hover:border-blue-200 hover:bg-blue-50/30"
                    )}
                    onClick={() => handleProviderSelect(provider.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={provider.avatar} alt={provider.name} />
                          <AvatarFallback>{provider.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                        </Avatar>
                        <div>
                          <h4 className="font-medium">{provider.name}</h4>
                          <div className="text-sm text-gray-600">{provider.specialty}</div>
                        </div>
                      </div>
                      <Badge variant="outline" className={
                        provider.availableIn === 'Available now' 
                          ? "bg-green-50 text-green-700 border-green-200" 
                          : "bg-blue-50 text-blue-700 border-blue-200"
                      }>
                        {provider.availableIn === 'Available now' 
                          ? <><CheckCircle className="w-3 h-3 mr-1" /> Now</> 
                          : <><Clock className="w-3 h-3 mr-1" /> {provider.availableIn}</>
                        }
                      </Badge>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <div>{renderStars(provider.rating)}</div>
                      <div className="flex gap-1">
                        {provider.badges?.map((badge, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {badge}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    
                    {selectedProvider === provider.id && (
                      <div className="pt-2 flex justify-end">
                        <Button 
                          size="sm" 
                          className="flex items-center gap-1"
                          onClick={handleConnect}
                          disabled={connectionInitiated}
                        >
                          <Video className="w-4 h-4" />
                          Connect Now
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium text-gray-700">What to expect:</h4>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span>Provider will review your symptoms: {symptoms.join(', ')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span>Consultation typically lasts 10-15 minutes</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span>Provider may recommend in-person care if needed</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span>You'll receive a summary and care plan after the call</span>
                </li>
              </ul>
            </div>
          </TabsContent>
          
          <TabsContent value="schedule" className="space-y-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <h3 className="font-medium text-lg mb-3">Schedule a Tele-Triage Appointment</h3>
              
              <div className="space-y-4">
                {scheduledProviders.map((provider) => (
                  <div 
                    key={provider.id} 
                    className={cn(
                      "border rounded-md p-3 space-y-2 cursor-pointer transition-colors",
                      selectedProvider === provider.id 
                        ? "border-blue-500 bg-blue-50" 
                        : "border-gray-200 hover:border-blue-200 hover:bg-blue-50/30"
                    )}
                    onClick={() => handleProviderSelect(provider.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={provider.avatar} alt={provider.name} />
                          <AvatarFallback>{provider.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                        </Avatar>
                        <div>
                          <h4 className="font-medium">{provider.name}</h4>
                          <div className="text-sm text-gray-600">{provider.specialty}</div>
                        </div>
                      </div>
                      <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                        <Calendar className="w-3 h-3 mr-1" /> {provider.availableIn}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <div>{renderStars(provider.rating)}</div>
                      <div className="flex gap-1">
                        {provider.badges?.map((badge, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {badge}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    
                    {selectedProvider === provider.id && (
                      <div className="pt-2 flex justify-end">
                        <Button 
                          size="sm" 
                          className="flex items-center gap-1"
                        >
                          <Calendar className="w-4 h-4" />
                          Book Appointment
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex items-center gap-2 bg-blue-50 p-3 rounded-md text-blue-800">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <span>
                {urgencyLevel === 'urgent' 
                  ? 'For urgent issues, we recommend connecting with an available provider now rather than scheduling.' 
                  : 'Schedule an appointment at a time that works for you.'}
              </span>
            </div>
          </TabsContent>
        </CardContent>
      </Tabs>
      
      <Separator />
      
      <CardFooter className="p-4 bg-gray-50">
        <div className="w-full text-center text-sm text-gray-600">
          If your symptoms worsen or you experience severe pain, difficulty breathing, or other emergency symptoms, 
          please call emergency services (911) or go to your nearest emergency room.
        </div>
      </CardFooter>
    </Card>
  );
}
